// api.ts — typed REST client for the openscan server (ARCHITECTURE.md §6).
// Every call is same-origin and carries the session cookie; failures throw an
// ApiError holding the HTTP status and the server's message so pages can tell
// "signed out" (401) from "gone" (404) from a real fault. Binary routes
// (splats, seed upload, keyframe upload) use ArrayBuffer/Blob/FormData.
// Every request goes through the shared createApiClient factory (STYLE.md
// Appendix A; lib/web/apiClient.ts). openscan's session is the HttpOnly
// oidcrp cookie, so no token is ever stored under its key; a 401 surfaces as
// an ApiError and useAuth treats it as signed out.

import { createApiClient } from 'common/apiClient';

export type ScanStatus = 'capturing' | 'refining' | 'ready' | 'failed';

export interface Intrinsics {
  width: number;
  height: number;
  fx: number;
  fy: number;
  cx: number;
  cy: number;
  k1: number;
  k2: number;
  p1: number;
  p2: number;
}

// Pose is world→camera, R row-major (Xc = R·Xw + T).
export interface Pose {
  R: number[];
  T: number[];
}

export interface MarkerPayload {
  id: number;
  size_m: number;
}

export interface Detection {
  payload: MarkerPayload;
  raw: string;
  corners: [number, number][]; // TL,TR,BR,BL image px
  modules: number;
}

export interface MarkerPlacement {
  id: number;
  size_m: number;
  pose: Pose; // marker→world
  observations: number;
}

export interface MarkerMap {
  ref_id: number;
  markers: Record<string, MarkerPlacement>;
}

export interface ScanDimensions {
  length_mm: number;
  width_mm: number;
  height_mm: number;
  volume_cm3: number;
}

export interface RefineInfo {
  iter: number;
  total_iters: number;
  psnr: number;
  phase: string;
}

// RefineQuality is POST /refine's quality: 'standard' (the server's
// default run) or 'max' (higher resolution, more splats and iterations;
// several times slower, GPU servers only — 409 elsewhere).
export type RefineQuality = 'standard' | 'max';

export interface Scan {
  id: string;
  owner: string;
  name: string;
  notes: string;
  tags: string[];
  pinned: boolean;
  status: ScanStatus;
  error: string;
  created_at: string;
  updated_at: string;
  keyframe_count: number;
  gaussian_count: number;
  has_seed: boolean;
  has_splats: boolean;
  intrinsics: Intrinsics | null;
  marker_map: MarkerMap | null;
  dimensions: ScanDimensions | null;
  refine: RefineInfo | null;
  // The refinement quality last asked for with refineScan: 'max' for the
  // opt-in max-quality preset, absent for standard. Kept across resumes
  // and keyframe-upload restarts.
  refine_quality?: RefineQuality;
  // Synthetic Enhancement: refines also train on renders of unseen
  // viewpoints cleaned by Difix. Stored with refineScan; absent = off.
  synthetic_enhancement?: boolean;
  // §14: the lens profile this scan was captured with, and whether its
  // intrinsics started from that profile or from self-calibration.
  device_key?: string;
  intrinsics_source?: 'profile' | 'selfcal' | '';
  device_update?: string; // what the last refine did to the profile
  // §15: where the scan was tracked, and the numbers behind the decision.
  processing?: ProcessingMode | '';
  camera_fps?: number;
  processing_fps?: number;
  reason?: string;
  video_truncated?: boolean;
  // Guided segmentation (§6): the user's named segments with seed points
  // and boxes, and exclude points, world metres. Absent after auto/box segmentation.
  guide?: Guide | null;
  // §16 lighting: whether the torch lit any keyframe, the exposure summary
  // and the fitted lighting (absent on scans refined without the model).
  flash_used?: boolean;
  exposure?: ScanExposure;
  lighting?: Lighting;
  // §15.6: the stored capture video (absent when none was uploaded).
  video?: VideoInfo;
  // Sharing (§6): the caller's role on this scan and whether they may run
  // compute (finish/refine/server video) on it. Present on GET /scans/{id}
  // and list items; live status frames omit them (mergeScan keeps them).
  access?: ScanAccess;
  can_compute?: boolean;
  shared?: boolean; // owner view only: any grants or links exist
  compute_by?: string; // who the last compute operation was charged to
}

// ---- Sharing and access (ARCHITECTURE.md §6) ----

export type ScanAccess = 'owner' | 'editor' | 'viewer';
export type ShareRole = 'viewer' | 'editor';

const ROLE_RANK: Record<ScanAccess, number> = { viewer: 1, editor: 2, owner: 3 };

// atLeast reports whether a scan's access is at least role. A scan without
// access (an older server) is treated as owned: that server had no sharing.
export function atLeast(scan: Pick<Scan, 'access'>, role: ScanAccess): boolean {
  return ROLE_RANK[scan.access ?? 'owner'] >= ROLE_RANK[role];
}

// mergeScan applies a live status frame (which carries no access fields)
// over the scan from the last GET, keeping the caller's access.
export function mergeScan(prev: Scan | null, next: Scan): Scan {
  if (!prev || prev.id !== next.id) return next;
  return {
    ...next,
    access: next.access ?? prev.access,
    can_compute: next.can_compute ?? prev.can_compute,
    shared: next.shared ?? prev.shared,
  };
}

export interface ShareGrant {
  email: string;
  role: ShareRole;
  granted_at: string;
  granted_by: string;
}

export interface ShareLink {
  id: string;
  role: ShareRole;
  token: string;
  label: string;
  created_at: string;
  created_by: string;
  expires_at: string | null;
  expired: boolean;
}

export interface Sharing {
  owner: string;
  grants: ShareGrant[];
  links: ShareLink[];
}

export interface UsageTotal {
  jobs: number;
  seconds: number;
}

export interface UsageCharge {
  at: string;
  kind: string;
  scan_id: string;
  scan_owner: string;
  foreign: boolean;
  seconds: number;
  outcome: string;
}

export interface Usage {
  principal: string;
  month: string;
  totals: Record<string, UsageTotal>;
  foreign: Record<string, UsageTotal>;
  recent: UsageCharge[];
}

// ---- Lighting (ARCHITECTURE.md §16) ----

// FlashState is a keyframe's torch tag: the commanded state after
// applyConstraints resolved, 'unknown' within FLASH_SETTLE_MS of a toggle.
export type FlashState = 'on' | 'off' | 'unknown';

// ExposureSample is the track's getSettings() exposure/white-balance state
// nearest a keyframe (snake_case; only the fields the browser exposes).
export interface ExposureSample {
  exposure_mode?: string;
  exposure_time?: number;
  iso?: number;
  exposure_compensation?: number;
  color_temperature?: number;
  white_balance_mode?: string;
  locked: boolean;
}

// FlashEvent is one applied torch toggle; t is performance.now() ms (the
// keyframe timestamp_ms / IMU sample clock).
export interface FlashEvent {
  t: number;
  on: boolean;
}

export interface ScanExposure {
  gain_range_ev: number;
  locked: boolean;
  jitter_ev?: number;
}

// Lighting is the scan's fitted environment: sh holds 9 SH-2 irradiance
// coefficients × RGB (sh[j*3+c], basis in splat/shading.ts).
export interface Lighting {
  sh: number[];
  flash_power?: number;
  flash_rgb?: [number, number, number];
  ambient_k?: number;
  key_dir?: [number, number, number];
  key_strength?: number;
}

// ---- Adaptive offload (ARCHITECTURE.md §15.3) ----

export type ProcessingMode = 'device' | 'server';

// VideoStartRequest is the POST …/video/start body: the recording format, the
// offload decision numbers and the lens-profile prior the server tracker
// should start from (the same one the phone session used).
export interface VideoStartRequest {
  mime: string;
  recorder_start_ms: number; // performance.now() at MediaRecorder.onstart
  camera_fps: number;
  processing_fps: number;
  reason: string;
  width: number;
  height: number;
  device_key?: string;
  prior_intrinsics?: NormIntrinsics;
  prior_sigma?: LensSigma;
  screen_angle: number;
  video_truncated?: boolean;
  // live asks the server to train a splat model on its GPU while the video
  // streams in (live mode, §15.7), when it has a live-capable worker; it then
  // streams `live` stats and the model's splats over /live. Old servers
  // ignore it. Sent only when the capture page's "Live splats" switch is on.
  live?: boolean;
}

export interface VideoStartResponse {
  upload_id: string;
}

export interface ImuBatchRequest {
  samples: { t: number; g: [number, number, number]; a: [number, number, number] }[];
  screen_angle: number;
}

// ---- Video archive (ARCHITECTURE.md §15.6) ----

// VideoInfo is Scan.video: the capture video the server keeps for the scan
// (device mode archives it; server mode stores what it re-tracked).
export interface VideoInfo {
  state: 'uploading' | 'complete' | 'partial';
  source: 'device' | 'server';
  mime?: string;
  file?: string;
  bytes: number;
  chunks: number;
  last_seq: number;
  missing?: number;
  recorder_start_ms?: number;
  width?: number;
  height?: number;
  truncated?: boolean;
}

// VideoArchiveStartRequest identifies the recording (mime +
// recorder_start_ms): the same pair resumes, returning the stored seqs.
export interface VideoArchiveStartRequest {
  mime: string;
  recorder_start_ms: number;
  width: number;
  height: number;
  bits_per_second?: number;
  screen_angle: number;
  device_key?: string;
}

export interface VideoArchiveStartResponse {
  upload_id: string; // '' when the video is already complete
  received: number[];
  video: VideoInfo | null;
}

export interface VideoArchiveFinishResponse {
  video: VideoInfo | null;
  missing: number[];
}

export interface Me {
  email: string;
  name: string;
  photo_url: string;
  scopes: string[];
  roles: string[];
  // library: may create and list own scans (openscan.user); compute: holds
  // openscan.compute; inference: holds inference.use (the CAD agent bills
  // the user's inference account, cad/AGENT.md §5). Absent on older servers.
  capabilities?: { library: boolean; compute: boolean; inference?: boolean };
  // inference mirrors capabilities.inference (AGENT.md §5 names it here).
  inference?: boolean;
}

// hasInference reports whether the signed-in user's token carries
// inference.use, from whichever field the server sent.
export function hasInference(me: Me | null | undefined): boolean {
  if (!me) return false;
  return Boolean(me.inference ?? me.capabilities?.inference ?? me.scopes?.includes('inference.use'));
}

// hasCompute reports whether the token carries openscan.compute.
export function hasCompute(me: Me | null | undefined): boolean {
  if (!me) return false;
  return Boolean(me.capabilities?.compute ?? me.scopes?.includes('openscan.compute'));
}

// ---- CAD agent (cad/AGENT.md §6) ----

export type AgentStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled' | 'budget';

export interface AgentJobSpec {
  scan_id?: string;
  model: string;
  instructions: string;
  budget_usd: number;
  max_turns?: number;
  max_subagents?: number;
  start_from?: number; // revision whose program seeds the file (0 = empty)
  mode?: 'guided' | 'free'; // guided (default): survey → plan → one subagent per feature → review
  inventory?: '' | 'off' | 'only'; // guided: '' runs the inventory (cuboids → identified parts) before the features
  inventory_model?: string; // the inventory's model (absent or '' = the server's default)
}

export type AgentPlanStatus = 'pending' | 'running' | 'kept' | 'reverted' | 'failed';

// AgentPlanItem is one planned feature of a guided run and its outcome.
export interface AgentPlanItem {
  name: string;
  what: string;
  kind: 'add' | 'cut' | 'detail' | string;
  region_mm: { min: number[]; max: number[] };
  status: AgentPlanStatus | string;
  delta_f: number;
  revision?: number;
  note?: string;
}

export interface AgentJob {
  id: string;
  spec: AgentJobSpec;
  actor: string;
  status: AgentStatus;
  created: string;
  updated: string;
  cost_usd: number;
  cost_source?: 'inference' | 'estimate' | string;
  turns: number;
  revision: number;
  f_score: number;
  summary: string;
  error?: string;
  steps: number;
  frame?: string; // "part": the model file is in the part frame
  plan?: AgentPlanItem[];
  cuboids?: AgentCuboid[]; // the inventory's inspection cuboids (AGENT.md §9.3)
  inventory?: AgentPart[]; // every part the inventory identified, #1..
}

// AgentCuboid is one part-frame box the inventory looked inside.
export interface AgentCuboid {
  name: string;
  feature: string;
  min: number[];
  max: number[];
  views: string[];
  look_for?: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped' | string;
  items: number;
  note?: string;
}

// AgentPart is one identified part: its label, kind and part-frame box.
export interface AgentPart {
  id: number;
  label: string;
  kind: string;
  feature: string;
  cuboid: string;
  min: number[];
  max: number[];
  snapped?: boolean; // the box is a measured relief bump's
}

export type AgentStepKind =
  | 'think'
  | 'tool'
  | 'build'
  | 'render'
  | 'edit'
  | 'subagent_start'
  | 'subagent_done'
  | 'note'
  | 'finish'
  | 'error'
  | 'survey'
  | 'plan'
  | 'revert';

export interface AgentStep {
  n: number;
  at: string;
  agent: string; // "main" or "sub:<feature>#k"
  kind: AgentStepKind;
  tool?: string;
  args?: unknown;
  text?: string;
  revision?: number;
  f_score?: number;
  images?: string[]; // "img/<step>-<k>.png", relative to the job
  file?: string; // "files/<step>.scad" when the file changed
  features?: string[];
  cost_usd?: number;
}

// CADRevision is one stored model revision (GET /api/scans/{id}/cad).
export interface CADRevision {
  rev: number;
  created_at: string;
  name?: string;
  source: 'program' | 'step' | 'scad' | string;
  parent?: number;
  fit?: { f_score?: number; tol_mm?: number } | null;
}

export interface AgentJobDetail {
  job: AgentJob;
  steps: AgentStep[];
}

export interface AgentModel {
  id: string;
  label: string;
  usd_per_m_in: number;
  usd_per_m_out: number;
  vision?: boolean;
}

// AgentRenderMode is a partial render of a step's file (AGENT.md §2.2).
export type AgentRenderMode = 'model' | `upto:${string}` | `only:${string}` | `without:${string}` | `highlight:${string}`;

export interface AgentRenderRequest {
  step: number;
  mode: AgentRenderMode;
  views?: { preset?: string; azimuth_deg?: number; elevation_deg?: number; zoom?: number }[];
  scan?: 'none' | 'overlay' | 'deviation';
}

// agentRunning reports whether a job can still change.
export function agentRunning(j: Pick<AgentJob, 'status'>): boolean {
  return j.status === 'queued' || j.status === 'running';
}

// featuresOf parses `// @feature <name>` markers (AGENT.md §2.1): name and
// 1-based start/end lines, for marking a feature in a step's file.
export function featuresOf(source: string): { name: string; start: number; end: number }[] {
  const lines = source.split('\n');
  const out: { name: string; start: number; end: number }[] = [];
  lines.forEach((l, i) => {
    const m = /^\s*\/\/\s*@feature\s+([a-z][a-z0-9_]{0,47})\b/.exec(l);
    if (m) {
      if (out.length) out[out.length - 1].end = i;
      out.push({ name: m[1], start: i + 1, end: lines.length });
    }
  });
  return out;
}

export interface KeyframePoint {
  x: number;
  y: number;
  point_id: number;
}

export interface MapPointJSON {
  id: number;
  pos: [number, number, number];
  color: [number, number, number];
  observations?: number;
}

// KeyframeMeta is produced by the WASM session (keyframeMeta) and uploaded
// verbatim beside the JPEG.
export interface KeyframeMeta {
  index: number;
  timestamp_ms: number;
  pose: Pose;
  intrinsics: Intrinsics;
  detections: Detection[];
  points: KeyframePoint[];
  map_points: MapPointJSON[];
  // §16, only with the opt-in flash lighting capture.
  flash?: FlashState;
  flash_changed_ms?: number;
  exposure?: ExposureSample;
}

// ViewWeight is a keyframe's photometric loss weight in the last refinement:
// weight = track clamped to [0.15, 1]. reproj_px is 0 when n_obs is 0;
// jump_rel is 0 when the path-jump test was not used. defocus is the
// fraction of the keyframe's pixels the trainer deweighted as out of focus
// and focus_weight their mean pixel weight (absent on refinements stored
// before focus weights existed).
export interface ViewWeight {
  index: number;
  weight: number;
  track: number;
  reproj_px: number;
  n_obs: number;
  jump_rel: number;
  defocus?: number;
  focus_weight?: number;
}

// ViewBlur is a keyframe's motion blur as the last refinement modelled it:
// the camera's path over the exposure (phi rad / delta m, camera axes),
// rendered as `samples` sub-poses per row in `bands` rolling-shutter row
// bands. smear_px is the predicted start-to-end smear in training pixels,
// smear_frac the same over the image's long side; source is the search
// candidate the path started from. When the refinement ran the lens model
// it also reports the keyframe's learned lens (absent otherwise; Go
// omitempty): exposure_s (s), focus_rho (focus plane, inverse depth 1/m; 0 =
// infinity), aperture_m (aperture coefficient α, m: defocus σ = α·fx·|1/z −
// focus_rho| training px), depth_m (median scene depth) and defocus_px (σ
// there, training px). phi/delta are then the lens's exposure path
// (exposure time × camera velocity).
export interface ViewBlur {
  index: number;
  smear_px: number;
  smear_frac: number;
  samples: number;
  bands: number;
  rot_deg: number;
  trans_mm: number;
  phi: [number, number, number];
  delta: [number, number, number];
  source: string;
  exposure_s?: number;
  focus_rho?: number;
  aperture_m?: number;
  depth_m?: number;
  defocus_px?: number;
}

// TrajectoryCamera is one keyframe's camera from GET /trajectory: the phone's
// SLAM pose and intrinsics, plus the bundle-adjusted ones and the loss
// weight once refined; blur only when that refinement modelled the
// keyframe's motion blur.
export interface TrajectoryCamera {
  index: number;
  timestamp_ms: number;
  pose: Pose;
  intrinsics: Intrinsics;
  refined_pose?: Pose;
  refined_intrinsics?: Intrinsics;
  weight?: ViewWeight;
  blur?: ViewBlur;
}

export interface Trajectory {
  cameras: TrajectoryCamera[];
  refined: boolean;
  rms_px?: number;
  // rolling_shutter is the refinement's rolling-shutter readout over
  // exposure (absent = not modelled).
  rolling_shutter?: number;
}

export interface BoxM {
  min: [number, number, number];
  max: [number, number, number];
}

export interface DimensionsResult {
  length_mm: number;
  width_mm: number;
  height_mm: number;
  volume_cm3: number;
  count: number;
  aabb: BoxM;
  obb: { center: number[]; axes: number[][]; half_extents: number[] };
}

export interface DistanceResult {
  distance_mm: number;
  a: [number, number, number]; // snapped, metres
  b: [number, number, number];
}

export interface SectionResult {
  width_mm: number;
  depth_mm: number;
  area_mm2: number;
  perimeter_mm: number;
  hull: [number, number][]; // mm
}

export interface SegmentStats {
  object: number;
  background: number;
  plane: number;
  component_count?: number;
  [k: string]: number | undefined;
}

export interface SegmentResult {
  stats: SegmentStats;
  dimensions: DimensionsResult | null;
}

export type Vec3M = [number, number, number];

// GuideSegment is one named segment the user seeded by tapping the model
// and/or fenced with a box. It needs a seed or a box.
export interface GuideSegment {
  id: number; // ≥1, unique, stable across edits
  name: string;
  seeds: Vec3M[]; // world metres
  // World-metre box: the segment never grows outside it; with no seeds it
  // takes everything inside it above the support plane.
  box?: BoxM;
}

export interface Guide {
  segments: GuideSegment[];
  exclude?: Vec3M[]; // world metres; regions they reach belong to no segment
}

// SegmentInfo is one guided segment computed on the scan's current cloud.
export interface SegmentInfo {
  id: number;
  name: string;
  count: number;
  seeds: number;
  seeds_matched: number;
  dimensions: DimensionsResult | null;
  gaussian_ids?: number[]; // with getSegments(…, {ids: true})
}

export interface SegmentsResult {
  guide: Guide | null;
  segments: SegmentInfo[];
  excluded: number;
  unmatched: Vec3M[]; // seeds that landed on nothing (plane or empty space)
  source?: 'splats' | 'seed';
}

export interface GuidedSegmentResult extends SegmentResult {
  segments: SegmentsResult;
}

export type SegmentRequest = { mode: 'auto' } | { mode: 'box'; box: BoxM } | { mode: 'guided'; guide: Guide };

export type LabelName = 'object' | 'background' | 'plane' | 'unknown';

// code is the server's machine-readable reason, when it sent one:
// 'forbidden' (role too low), 'compute_scope_required', 'quota_exceeded'.
export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly code = '') {
    super(message);
    this.name = 'ApiError';
  }
}

export const apiClient = createApiClient({ tokenKey: 'openscan_token' });

// ---- Share-link tokens ----
//
// A share link is /scans/{id}?share={token}. The token rides on every request
// for that scan: the X-Openscan-Share header on fetches, and ?share= on URLs
// the browser loads itself (img src, download hrefs, the WebSocket). Tokens
// are kept per scan id in memory and mirrored to sessionStorage so a reload
// keeps working; storage may be unavailable (private mode), so every access
// is guarded and memory alone still works.

export const SHARE_HEADER = 'X-Openscan-Share';
export const SHARE_PARAM = 'share';
const SHARE_STORAGE_PREFIX = 'openscan_share:';
const shareTokens = new Map<string, string>();

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

// setShareToken remembers token for scan id ('' forgets it).
export function setShareToken(id: string, token: string): void {
  if (token) shareTokens.set(id, token);
  else shareTokens.delete(id);
  try {
    const st = storage();
    if (!st) return;
    if (token) st.setItem(SHARE_STORAGE_PREFIX + id, token);
    else st.removeItem(SHARE_STORAGE_PREFIX + id);
  } catch {
    // storage full or blocked: the in-memory token still applies
  }
}

// shareToken returns the token held for scan id, or ''.
export function shareToken(id: string): string {
  const mem = shareTokens.get(id);
  if (mem) return mem;
  try {
    const v = storage()?.getItem(SHARE_STORAGE_PREFIX + id) ?? '';
    if (v) shareTokens.set(id, v);
    return v;
  } catch {
    return '';
  }
}

// adoptShareToken reads ?share= from a location search string and stores it
// for scan id; returns the token held for id afterwards.
export function adoptShareToken(id: string, search: string): string {
  const t = new URLSearchParams(search).get(SHARE_PARAM) ?? '';
  if (t) setShareToken(id, t);
  return shareToken(id);
}

// shareUrl is the link a share token opens.
export function shareUrl(id: string, token: string): string {
  return `${window.location.origin}/scans/${encodeURIComponent(id)}?${SHARE_PARAM}=${encodeURIComponent(token)}`;
}

const SCAN_PATH_RE = /^\/api\/scans\/([^/?#]+)/;

// scanIdOf extracts the scan id from an /api/scans/{id}… path.
export function scanIdOf(path: string): string {
  const m = SCAN_PATH_RE.exec(path);
  return m ? decodeURIComponent(m[1]) : '';
}

// withShareQuery appends ?share= for the scan the URL names, if a token is
// held for it.
export function withShareQuery(url: string): string {
  let path = url;
  try {
    path = new URL(url, 'http://x').pathname;
  } catch {
    // relative path as is
  }
  const id = scanIdOf(path);
  const t = id ? shareToken(id) : '';
  if (!t) return url;
  return `${url}${url.includes('?') ? '&' : '?'}${SHARE_PARAM}=${encodeURIComponent(t)}`;
}

// withShareHeader adds the share header to init when path names a scan
// whose token is held.
export function withShareHeader(path: string, init: RequestInit = {}): RequestInit {
  const id = scanIdOf(path);
  const t = id ? shareToken(id) : '';
  if (!t) return init;
  // A plain record: common/apiClient spreads init.headers as an object, which
  // would drop a Headers instance's entries.
  const headers: Record<string, string> = {};
  new Headers(init.headers).forEach((v, k) => {
    headers[k] = v;
  });
  headers[SHARE_HEADER] = t;
  return { ...init, headers };
}

// computeLoginUrl is the elevated sign-in that also asks for openscan.compute.
export function computeLoginUrl(returnTo: string): string {
  return `/api/oidc/compute-login?return_to=${encodeURIComponent(returnTo)}`;
}

// inferenceLoginUrl is the elevated sign-in that also asks for inference.use
// (AGENT.md §5); compute keeps openscan.compute for a user who had it.
export function inferenceLoginUrl(returnTo: string, compute = false): string {
  return `/api/oidc/inference-login?${compute ? 'compute=1&' : ''}return_to=${encodeURIComponent(returnTo)}`;
}

async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const method = init.method ?? 'GET';
  const t0 = performance.now();
  const res = await apiClient.fetch(path, withShareHeader(path, init));
  const ms = Math.round(performance.now() - t0);
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    let code = '';
    try {
      const text = await res.text();
      if (text) {
        try {
          const j = JSON.parse(text) as { error?: string; message?: string; code?: string };
          msg = j.error || j.message || text;
          code = j.code ?? '';
        } catch {
          msg = text;
        }
      }
    } catch {
      // body unreadable: keep the status line
    }
    console.warn(`[openscan/api] ${method} ${path} → ${res.status}${code ? ` ${code}` : ''} (${ms} ms): ${msg}`);
    throw new ApiError(res.status, msg, code);
  }
  console.debug(`[openscan/api] ${method} ${path} → ${res.status} (${ms} ms)`);
  return res;
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await request(path, init);
  return (await res.json()) as T;
}

function jsonBody(method: string, body: unknown): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
}

const scanPath = (id: string): string => `/api/scans/${encodeURIComponent(id)}`;

// normalizeSharing tolerates null slices.
function normalizeSharing(s: Sharing): Sharing {
  return { owner: s.owner ?? '', grants: s.grants ?? [], links: s.links ?? [] };
}
// uploadQuery is the optional ?upload_id= of the §15 video routes.
const uploadQuery = (uploadId: string): string => (uploadId ? `?upload_id=${encodeURIComponent(uploadId)}` : '');

function labelsQuery(labels?: readonly LabelName[], version?: number): string {
  const parts: string[] = [];
  if (labels && labels.length) parts.push(`labels=${labels.join(',')}`);
  // §16: version=2 asks for OSPL v2 (material); older servers ignore it.
  if (version && version !== 1) parts.push(`version=${version}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const api = {
  me: (): Promise<Me> => json<Me>('/api/me'),

  // listScans lists the caller's own scans (view 'mine', needs the library
  // capability) or those other owners shared with them ('shared').
  listScans: async (q = '', view: 'mine' | 'shared' = 'mine'): Promise<Scan[]> => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (view !== 'mine') p.set('view', view);
    const qs = p.toString();
    const r = await json<{ scans: Scan[] | null }>(`/api/scans${qs ? `?${qs}` : ''}`);
    return r.scans ?? [];
  },

  usage: (): Promise<Usage> => json<Usage>('/api/usage'),

  // Sharing (owner only). Every call answers the scan's whole Sharing.
  getSharing: async (id: string): Promise<Sharing> => normalizeSharing(await json<Sharing>(`${scanPath(id)}/sharing`)),

  grantAccess: async (id: string, email: string, role: ShareRole): Promise<Sharing> =>
    normalizeSharing(await json<Sharing>(`${scanPath(id)}/sharing/grants`, jsonBody('POST', { email, role }))),

  revokeAccess: async (id: string, email: string): Promise<Sharing> =>
    normalizeSharing(await json<Sharing>(`${scanPath(id)}/sharing/grants/${encodeURIComponent(email)}`, { method: 'DELETE' })),

  // createLink mints a share link; expiresInHours 0 never expires.
  createLink: async (id: string, role: ShareRole, label = '', expiresInHours = 0): Promise<Sharing> =>
    normalizeSharing(
      await json<Sharing>(
        `${scanPath(id)}/sharing/links`,
        jsonBody('POST', { role, ...(label ? { label } : {}), ...(expiresInHours > 0 ? { expires_in_hours: expiresInHours } : {}) }),
      ),
    ),

  revokeLink: async (id: string, linkId: string): Promise<Sharing> =>
    normalizeSharing(await json<Sharing>(`${scanPath(id)}/sharing/links/${encodeURIComponent(linkId)}`, { method: 'DELETE' })),

  // createScan optionally registers the capturing device (§14); the server
  // creates its lens profile lazily.
  createScan: (name?: string, device?: DeviceRegistration): Promise<Scan> =>
    json<Scan>('/api/scans', jsonBody('POST', { ...(name ? { name } : {}), ...(device ?? {}) })),

  getScan: (id: string): Promise<Scan> => json<Scan>(scanPath(id)),

  patchScan: (id: string, patch: Partial<Pick<Scan, 'name' | 'notes' | 'tags' | 'pinned'>>): Promise<Scan> =>
    json<Scan>(scanPath(id), jsonBody('PATCH', patch)),

  deleteScan: async (id: string): Promise<void> => {
    await request(scanPath(id), { method: 'DELETE' });
  },

  // uploadKeyframe posts the multipart pair: `meta` (KeyframeMeta JSON) and
  // `image` (the full-resolution JPEG).
  uploadKeyframe: async (id: string, meta: KeyframeMeta | string, jpeg: Blob): Promise<{ index: number }> => {
    const form = new FormData();
    // `meta` goes as a plain form field (r.FormValue on the server), not a file part.
    form.append('meta', typeof meta === 'string' ? meta : JSON.stringify(meta));
    form.append('image', jpeg, 'keyframe.jpg');
    return json<{ index: number }>(`${scanPath(id)}/keyframes`, { method: 'POST', body: form });
  },

  // getTrajectory lists every keyframe's camera in index order (§6).
  getTrajectory: (id: string): Promise<Trajectory> => json<Trajectory>(`${scanPath(id)}/trajectory`),

  putSeed: async (id: string, ospl: ArrayBuffer | Uint8Array): Promise<void> => {
    let body: ArrayBuffer;
    if (ospl instanceof Uint8Array) {
      const copy = new Uint8Array(ospl.byteLength); // detach from any shared/offset buffer
      copy.set(ospl);
      body = copy.buffer;
    } else {
      body = ospl;
    }
    await request(`${scanPath(id)}/seed`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body,
    });
  },

  finishScan: (id: string): Promise<Scan> => json<Scan>(`${scanPath(id)}/finish`, { method: 'POST' }),

  // syntheticEnhancement turns Synthetic Enhancement on or off for this and
  // later refines (409 when no worker has the Difix service); undefined
  // keeps the scan's setting.
  refineScan: (id: string, quality: RefineQuality = 'standard', syntheticEnhancement?: boolean): Promise<Scan> =>
    json<Scan>(
      `${scanPath(id)}/refine`,
      jsonBody('POST', syntheticEnhancement === undefined ? { quality } : { quality, synthetic_enhancement: syntheticEnhancement }),
    ),

  // §15.3 server-mode video upload: start, idempotent per-seq chunk PUTs,
  // IMU batches on the recorder clock, then finish with the last seq.
  videoStart: (id: string, body: VideoStartRequest): Promise<VideoStartResponse> =>
    json<VideoStartResponse>(`${scanPath(id)}/video/start`, jsonBody('POST', body)),

  // putVideoChunk / videoFinish pass the upload id from video/start as
  // ?upload_id= (§15.4.1): a chunk for a replaced upload answers 409 instead
  // of being spliced into the new recording. seq starts at 0.
  putVideoChunk: async (id: string, seq: number, chunk: Blob, uploadId = ''): Promise<void> => {
    await request(`${scanPath(id)}/video/chunks/${seq}${uploadQuery(uploadId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: chunk,
    });
  },

  postImu: async (id: string, body: ImuBatchRequest): Promise<void> => {
    await request(`${scanPath(id)}/imu`, jsonBody('POST', body));
  },

  // postFlash appends applied torch toggles to the scan's flash log (§16.1);
  // the server tags device keyframes and decoded video frames from it.
  postFlash: async (id: string, events: FlashEvent[]): Promise<void> => {
    await request(`${scanPath(id)}/flash`, jsonBody('POST', { events }));
  },

  // postExposure appends camera exposure samples ({t, ...ExposureSample},
  // t on the performance.now() clock) to the scan's exposure log.
  postExposure: async (id: string, samples: Array<ExposureSample & { t: number }>): Promise<void> => {
    await request(`${scanPath(id)}/exposure`, jsonBody('POST', { samples }));
  },

  // videoFinish (last_seq −1 = no chunks) answers 202 with the Scan, which
  // stays `capturing` while the server drains the video and only then goes
  // `refining` (§15.4.1); null for a bodiless reply.
  videoFinish: async (id: string, lastSeq: number, uploadId = ''): Promise<Scan | null> => {
    const res = await request(`${scanPath(id)}/video/finish${uploadQuery(uploadId)}`, jsonBody('POST', { last_seq: lastSeq }));
    const text = res.status === 204 ? '' : await res.text();
    if (!text) return null;
    const v = JSON.parse(text) as Partial<Scan>;
    return typeof v.id === 'string' && typeof v.status === 'string' ? (v as Scan) : null;
  },

  // §15.6 device-mode video archive: store-only (nothing re-tracks it), any
  // scan status, chunks/imu/finish carry ?upload_id= from archive/start.
  videoArchiveStart: (id: string, body: VideoArchiveStartRequest): Promise<VideoArchiveStartResponse> =>
    json<VideoArchiveStartResponse>(`${scanPath(id)}/video/archive/start`, jsonBody('POST', body)),

  putVideoArchiveChunk: async (id: string, seq: number, chunk: Blob, uploadId: string): Promise<void> => {
    await request(`${scanPath(id)}/video/archive/chunks/${seq}${uploadQuery(uploadId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: chunk,
    });
  },

  postVideoArchiveImu: async (id: string, body: ImuBatchRequest, uploadId: string): Promise<void> => {
    await request(`${scanPath(id)}/video/archive/imu${uploadQuery(uploadId)}`, jsonBody('POST', body));
  },

  videoArchiveFinish: (id: string, lastSeq: number, truncated: boolean, uploadId: string): Promise<VideoArchiveFinishResponse> =>
    json<VideoArchiveFinishResponse>(
      `${scanPath(id)}/video/archive/finish${uploadQuery(uploadId)}`,
      jsonBody('POST', { last_seq: lastSeq, ...(truncated ? { video_truncated: true } : {}) }),
    ),


  // getSplats returns OSPL bytes; version 2 requests the §16 material
  // records (the server answers v1 when the scan has none, or is older).
  getSplats: async (id: string, labels?: readonly LabelName[], version?: number): Promise<ArrayBuffer> => {
    const res = await request(`${scanPath(id)}/splats${labelsQuery(labels, version)}`);
    return res.arrayBuffer();
  },

  segment: (id: string, body: SegmentRequest): Promise<SegmentResult> =>
    json<SegmentResult>(`${scanPath(id)}/segment`, jsonBody('POST', body)),

  segmentGuided: (id: string, guide: Guide): Promise<GuidedSegmentResult> =>
    json<GuidedSegmentResult>(`${scanPath(id)}/segment`, jsonBody('POST', { mode: 'guided', guide })),

  // getSegments re-derives the stored guide's segments on the current cloud;
  // ids adds each segment's member gaussian ids (for the viewer's tint).
  getSegments: (id: string, opts: { ids?: boolean; dimensions?: boolean } = {}): Promise<SegmentsResult> => {
    const q = new URLSearchParams();
    if (opts.ids) q.set('ids', '1');
    if (opts.dimensions === false) q.set('dimensions', '0');
    const qs = q.toString();
    return json<SegmentsResult>(`${scanPath(id)}/segments${qs ? `?${qs}` : ''}`);
  },

  measureDimensions: (id: string): Promise<DimensionsResult> =>
    json<DimensionsResult>(`${scanPath(id)}/measure`, jsonBody('POST', { kind: 'dimensions' })),

  measureDistance: (id: string, a: number[], b: number[], snap = true): Promise<DistanceResult> =>
    json<DistanceResult>(`${scanPath(id)}/measure`, jsonBody('POST', { kind: 'distance', a, b, snap })),

  measureSection: (id: string, axis: 'x' | 'y' | 'z', atMm: number, thicknessMm = 2): Promise<SectionResult> =>
    json<SectionResult>(`${scanPath(id)}/measure`, jsonBody('POST', { kind: 'section', axis, at_mm: atMm, thickness_mm: thicknessMm })),

  // listCAD lists the scan's stored CAD revisions, oldest first.
  listCAD: async (id: string): Promise<CADRevision[]> => {
    const r = await json<{ revisions: CADRevision[] | null }>(`${scanPath(id)}/cad`);
    return r.revisions ?? [];
  },

  // ---- CAD agent (cad/AGENT.md §6.5) ----
  agentModels: async (id: string): Promise<{ models: AgentModel[]; default: string; inventoryDefault: string }> => {
    const r = await json<{ models: AgentModel[] | null; default: string; inventory_default?: string }>(`${scanPath(id)}/agent/models`);
    return { models: r.models ?? [], default: r.default, inventoryDefault: r.inventory_default ?? '' };
  },

  listAgentJobs: async (id: string): Promise<AgentJob[]> => {
    const r = await json<{ jobs: AgentJob[] | null }>(`${scanPath(id)}/agent`);
    return r.jobs ?? [];
  },

  startAgent: (id: string, spec: AgentJobSpec): Promise<AgentJob> => json<AgentJob>(`${scanPath(id)}/agent`, jsonBody('POST', spec)),

  // getAgentJob returns the job and its steps; after > 0 returns only newer steps.
  getAgentJob: async (id: string, job: string, after = 0): Promise<AgentJobDetail> => {
    const r = await json<{ job: AgentJob; steps: AgentStep[] | null }>(
      `${scanPath(id)}/agent/${encodeURIComponent(job)}${after > 0 ? `?after=${after}` : ''}`,
    );
    return { job: r.job, steps: r.steps ?? [] };
  },

  cancelAgent: (id: string, job: string): Promise<AgentJob> =>
    json<AgentJob>(`${scanPath(id)}/agent/${encodeURIComponent(job)}/cancel`, { method: 'POST' }),

  // renderAgentStep renders a step's file (whole, up to/only/highlight a
  // feature) and answers a PNG blob.
  renderAgentStep: async (id: string, job: string, req: AgentRenderRequest): Promise<Blob> => {
    const res = await request(`${scanPath(id)}/agent/${encodeURIComponent(job)}/render`, jsonBody('POST', req));
    return res.blob();
  },

  // agentFile fetches a step's .scad ("files/<step>.scad").
  agentFile: async (id: string, job: string, file: string): Promise<string> => {
    const res = await request(`${scanPath(id)}/agent/${encodeURIComponent(job)}/${file.split('/').map(encodeURIComponent).join('/')}`);
    return res.text();
  },
};

// URL builders for <img>/<a download> targets.
// Every per-scan URL carries the scan's share token (?share=) when one is held.
export const urls = {
  splats: (id: string, labels?: readonly LabelName[]): string => withShareQuery(`${scanPath(id)}/splats${labelsQuery(labels)}`),
  exportPly: (id: string, labels?: readonly LabelName[]): string => withShareQuery(`${scanPath(id)}/export.ply${labelsQuery(labels)}`),
  segmentPly: (id: string, segmentId: number): string => withShareQuery(`${scanPath(id)}/segments/${segmentId}/export.ply`),
  segmentsZip: (id: string): string => withShareQuery(`${scanPath(id)}/segments/export.zip`),
  video: (id: string): string => withShareQuery(`${scanPath(id)}/video`), // §15.6 capture video, 404 until complete
  thumbnail: (id: string, version = ''): string =>
    withShareQuery(`${scanPath(id)}/thumbnail.jpg${version ? `?v=${encodeURIComponent(version)}` : ''}`),
  keyframe: (id: string, n: number): string => withShareQuery(`${scanPath(id)}/keyframes/${n}.jpg`),
  live: (id: string): string => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}${withShareQuery(`${scanPath(id)}/live`)}`;
  },
  mcp: (): string => `${window.location.origin}/mcp`,
  // CAD agent artefacts (AGENT.md §6.5, §7): a step's image ("img/<f>.png"),
  // the latest file, and a revision's downloads.
  agentImage: (id: string, job: string, img: string): string =>
    withShareQuery(`${scanPath(id)}/agent/${encodeURIComponent(job)}/${img.split('/').map(encodeURIComponent).join('/')}`),
  agentModelScad: (id: string, job: string): string => withShareQuery(`${scanPath(id)}/agent/${encodeURIComponent(job)}/model.scad`),
  cadScad: (id: string, rev: number): string => withShareQuery(`${scanPath(id)}/cad/${rev}/program.scad`),
  cadStl: (id: string, rev: number): string => withShareQuery(`${scanPath(id)}/cad/${rev}/model.stl`),
  cadStep: (id: string, rev: number): string => withShareQuery(`${scanPath(id)}/cad/${rev}/model.step`),
};

// ---- Device lens profiles (ARCHITECTURE.md §14) ----

// Normalized intrinsics: fx/W, fy/H, cx/W, cy/H, plus distortion.
export interface NormIntrinsics {
  fx: number;
  fy: number;
  cx: number;
  cy: number;
  k1: number;
  k2: number;
  p1: number;
  p2: number;
}

// One-sigma uncertainty: relative focal, principal point in px of the
// profile's own width×height, and the radial terms.
export interface LensSigma {
  f_rel: number;
  c_px: number;
  k1: number;
  k2: number;
}

// One scan's lens measurement as the server recorded it (server/devices
// Estimate). `outcome` says what the fusion did with it.
// 'replaced' / 'duplicate' are re-refines of a scan the profile already saw.
export type EstimateOutcome = 'initial' | 'fused' | 'pending' | 'rebased' | 'replaced' | 'duplicate' | 'locked' | 'rejected';

export interface DeviceEstimate {
  scan_id: string;
  width: number;
  height: number;
  intrinsics: NormIntrinsics;
  sigma: LensSigma;
  rms_px: number;
  keyframes: number;
  at: string;
  outcome: EstimateOutcome | string;
}

export interface DeviceProfile {
  key: string;
  fingerprint: string;
  label: string;
  width: number;
  height: number;
  zoom: number;
  intrinsics: NormIntrinsics | null;
  sigma: LensSigma | null;
  scans: string[]; // scans fused into the current estimate
  n: number;
  updated_at: string;
  locked: boolean;
  hfov_deg?: number;
  created_at?: string;
  pending?: DeviceEstimate[]; // outlier candidates awaiting agreement
  history?: DeviceEstimate[]; // every estimate seen, newest last
}

export interface DeviceRegistration {
  device_key: string;
  device_label: string;
  fingerprint: string;
  intrinsics_source?: 'profile' | 'selfcal';
}

const devicePath = (key: string): string => `/api/devices/${encodeURIComponent(key)}`;

// normalizeProfile tolerates null slices and missing optional members.
export function normalizeProfile(p: DeviceProfile): DeviceProfile {
  return { ...p, scans: p.scans ?? [], n: p.n ?? 0, locked: !!p.locked, zoom: p.zoom || 1, history: p.history ?? [], pending: p.pending ?? [] };
}

async function profileOrNull(path: string, init: RequestInit): Promise<DeviceProfile | null> {
  const res = await request(path, init);
  const text = res.status === 204 ? '' : await res.text();
  return text ? normalizeProfile(JSON.parse(text) as DeviceProfile) : null;
}

export const devicesApi = {
  list: async (): Promise<DeviceProfile[]> => {
    const r = await json<{ devices?: DeviceProfile[] | null; profiles?: DeviceProfile[] | null } | DeviceProfile[]>('/api/devices');
    const list = Array.isArray(r) ? r : (r.devices ?? r.profiles ?? []);
    return list.map(normalizeProfile);
  },

  // lookup resolves null on 404 (no profile for this key yet). Chromium
  // logs that 404 as a console error, so Capture uses find() instead.
  lookup: async (key: string): Promise<DeviceProfile | null> => {
    try {
      return normalizeProfile(await json<DeviceProfile>(`/api/devices/lookup?key=${encodeURIComponent(key)}`));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  // patch and reset return the updated profile, or null on a bodiless 204.
  // find looks the key up in the owner's list: same answer as lookup, but a
  // device without a profile is an empty result rather than a logged 404.
  find: async (key: string): Promise<DeviceProfile | null> => {
    const list = await devicesApi.list();
    return list.find((p) => p.key === key) ?? null;
  },

  patch: (key: string, patch: { label?: string; locked?: boolean }): Promise<DeviceProfile | null> =>
    profileOrNull(devicePath(key), jsonBody('PATCH', patch)),

  reset: (key: string): Promise<DeviceProfile | null> => profileOrNull(`${devicePath(key)}/reset`, { method: 'POST' }),

  remove: async (key: string): Promise<void> => {
    await request(devicePath(key), { method: 'DELETE' });
  },
};
